import type { TFunction } from 'i18next'
import type { ComplianceDocumentSectionRecord } from '@artem-lex/compliance-documents'
import { formatRecyclabilityAssessmentDocumentSectionBlock } from '@artem-lex/compliance-documents'
import type {
  PackagingComponent,
  PackagingRecyclabilityAssessmentStoredEntry,
} from '@/shared/hooks/api/packagingSuppliers'
import type { ComponentEditorLabels } from '@/shared/components/packaging/componentEditorTypes'
import {
  getMaterialLabel,
  getRecyclingStreamLabel,
} from '@/shared/components/packaging/componentEditorUtils'
import { formatComponentWeightGramsDisplay } from '@/shared/components/packaging/componentWeightGrams'
import {
  formatAnnex1Percentage,
  pickAnnex1ProseLocale,
  resolveRecyclabilityAssessmentDocumentAnnex1Values,
  type RecyclabilityAssessmentDocumentAnnex1Prose,
} from './recyclabilityAssessmentDocumentAnnex1Helpers'
import {
  RECYCLABILITY_ASSESSMENT_DOCUMENT_SECTION_3_GROUPS,
  resolveRecyclabilityAssessmentDocumentSection3Response,
  type RecyclabilityAssessmentDocumentProseByCriterion,
} from './recyclabilityAssessmentDocumentSection3Helpers'

function escapeMarkdownTableCell(value: string): string {
  return value
    .replace(/\|/g, '\\|')
    .replace(/\r\n|\r|\n/g, ' ')
    .trim()
}

function formatWeightPercentage(weightAsPercentage: number | undefined): string {
  if (weightAsPercentage === undefined || !Number.isFinite(weightAsPercentage)) {
    return '—'
  }
  return `${weightAsPercentage.toFixed(1)}%`
}

function buildMarkdownTable(headers: string[], rows: string[][]): string {
  const headerLine = `| ${headers.map(escapeMarkdownTableCell).join(' | ')} |`
  const separatorLine = `| ${headers.map(() => '---').join(' | ')} |`
  const dataLines = rows.map(
    (row) => `| ${row.map(escapeMarkdownTableCell).join(' | ')} |`
  )
  return [headerLine, separatorLine, ...dataLines].join('\n')
}

export function buildRecyclabilityAssessmentPurposeBodyMarkdown(
  translate: TFunction<'packagingSuppliers'>
): string {
  return translate('itemDetail.recyclability.document.purposeBody')
}

export function buildRecyclabilityAssessmentCompositionBodyMarkdown(
  translate: TFunction<'packagingSuppliers'>,
  components: PackagingComponent[],
  labels: ComponentEditorLabels
): string {
  if (components.length === 0) {
    return translate('itemDetail.recyclability.document.compositionEmpty')
  }

  const headers = [
    translate('itemDetail.recyclability.document.columnComponent'),
    translate('itemDetail.recyclability.document.columnMaterialType'),
    translate('itemDetail.recyclability.document.columnWeightGrams'),
    translate('itemDetail.recyclability.document.columnWeightPercentage'),
    translate('itemDetail.recyclability.document.columnRecyclingStream'),
  ]
  const rows = components.map((component) => [
    component.name.trim() || '—',
    getMaterialLabel(component.material, labels),
    formatComponentWeightGramsDisplay(component.weightInGrams),
    formatWeightPercentage(component.weightAsPercentage),
    getRecyclingStreamLabel(component.recyclingStream, labels),
  ])

  return buildMarkdownTable(headers, rows)
}

export function buildRecyclabilityAssessmentSection3BodyMarkdown(
  translate: TFunction<'packagingSuppliers'>,
  locale: string,
  generatedProse: RecyclabilityAssessmentDocumentProseByCriterion | null
): string {
  const headers = [
    translate('itemDetail.recyclability.document.section3.columnRef'),
    translate('itemDetail.recyclability.document.section3.columnCriteria'),
    translate('itemDetail.recyclability.document.section3.columnResponse'),
  ]
  const rows: string[][] = []

  for (const group of RECYCLABILITY_ASSESSMENT_DOCUMENT_SECTION_3_GROUPS) {
    rows.push([translate(group.titleKey), '', ''])
    for (const row of group.rows) {
      rows.push([
        row.ref,
        translate(row.criteriaKey),
        resolveRecyclabilityAssessmentDocumentSection3Response(
          row,
          translate,
          locale,
          generatedProse
        ),
      ])
    }
  }

  return buildMarkdownTable(headers, rows)
}

export function buildRecyclabilityAssessmentAnnex1BodyMarkdown(
  translate: TFunction<'packagingSuppliers'>,
  locale: string,
  itemName: string,
  components: PackagingComponent[],
  history: PackagingRecyclabilityAssessmentStoredEntry[] | undefined,
  labels: ComponentEditorLabels,
  annex1Prose: RecyclabilityAssessmentDocumentAnnex1Prose | null
): string {
  const values = resolveRecyclabilityAssessmentDocumentAnnex1Values({
    itemName,
    components,
    history,
    labels,
  })

  const collectionSorting =
    annex1Prose != null
      ? pickAnnex1ProseLocale(
          locale,
          annex1Prose.constituentsCollectionSorting
        )
      : '—'
  const recycling =
    annex1Prose != null
      ? pickAnnex1ProseLocale(locale, annex1Prose.constituentsRecycling)
      : '—'
  const negativeInfluence =
    annex1Prose != null
      ? pickAnnex1ProseLocale(
          locale,
          annex1Prose.constituentsNegativeInfluence
        )
      : '—'

  const headers = [
    translate('itemDetail.recyclability.document.annex1.columnFunctionalUnit'),
    translate('itemDetail.recyclability.document.annex1.columnComponent'),
  ]
  const rows: string[][] = [
    [
      translate('itemDetail.recyclability.document.annex1.line1'),
      values.description,
    ],
    [
      translate('itemDetail.recyclability.document.annex1.line2'),
      formatAnnex1Percentage(values.weightAsPercentageOfFunctionalUnit),
    ],
    [
      translate('itemDetail.recyclability.document.annex1.line3'),
      values.referencedStandards ?? '—',
    ],
    [
      translate('itemDetail.recyclability.document.annex1.line4'),
      values.intendedRecyclingStreamLabel,
    ],
    [translate('itemDetail.recyclability.document.annex1.line5'), collectionSorting],
    [translate('itemDetail.recyclability.document.annex1.line6'), recycling],
    [translate('itemDetail.recyclability.document.annex1.line7'), negativeInfluence],
    [
      translate('itemDetail.recyclability.document.annex1.line8'),
      formatAnnex1Percentage(values.componentRecyclingPercentage),
    ],
    [
      translate('itemDetail.recyclability.document.annex1.line9'),
      formatAnnex1Percentage(values.functionalUnitRecyclingPercentage),
    ],
    [
      translate('itemDetail.recyclability.document.annex1.line10'),
      formatAnnex1Percentage(values.totalRecyclingPercentage),
    ],
  ]

  return [
    translate('itemDetail.recyclability.document.annex1.subtitle'),
    '',
    buildMarkdownTable(headers, rows),
  ].join('\n')
}

export function buildRecyclabilityAssessmentDocumentSections(params: {
  translate: TFunction<'packagingSuppliers'>
  locale: string
  itemName: string
  components: PackagingComponent[]
  history: PackagingRecyclabilityAssessmentStoredEntry[] | undefined
  labels: ComponentEditorLabels
  generatedProse: RecyclabilityAssessmentDocumentProseByCriterion | null
  annex1Prose: RecyclabilityAssessmentDocumentAnnex1Prose | null
}): ComplianceDocumentSectionRecord[] {
  return [
    {
      sectionId: '1',
      title: params.translate('itemDetail.recyclability.document.purposeSectionTitle'),
      kind: 'canonical',
      sortOrder: 0,
      markdown: buildRecyclabilityAssessmentPurposeBodyMarkdown(params.translate),
    },
    {
      sectionId: '2',
      title: params.translate('itemDetail.recyclability.document.compositionHeading'),
      kind: 'canonical',
      sortOrder: 1,
      markdown: buildRecyclabilityAssessmentCompositionBodyMarkdown(
        params.translate,
        params.components,
        params.labels
      ),
    },
    {
      sectionId: '3',
      title: params.translate('itemDetail.recyclability.document.section3.sectionTitle'),
      kind: 'canonical',
      sortOrder: 2,
      markdown: buildRecyclabilityAssessmentSection3BodyMarkdown(
        params.translate,
        params.locale,
        params.generatedProse
      ),
    },
    {
      sectionId: 'annex-1',
      title: params.translate('itemDetail.recyclability.document.annex1.heading'),
      kind: 'canonical',
      sortOrder: 3,
      markdown: buildRecyclabilityAssessmentAnnex1BodyMarkdown(
        params.translate,
        params.locale,
        params.itemName,
        params.components,
        params.history,
        params.labels,
        params.annex1Prose
      ),
    },
  ]
}

export function buildRecyclabilityAssessmentDocumentMarkdown(params: {
  translate: TFunction<'packagingSuppliers'>
  locale: string
  itemName: string
  components: PackagingComponent[]
  history: PackagingRecyclabilityAssessmentStoredEntry[] | undefined
  labels: ComponentEditorLabels
  generatedProse: RecyclabilityAssessmentDocumentProseByCriterion | null
  annex1Prose: RecyclabilityAssessmentDocumentAnnex1Prose | null
}): string {
  const sections = buildRecyclabilityAssessmentDocumentSections(params)
  return sections
    .map((section) => formatRecyclabilityAssessmentDocumentSectionBlock(section))
    .filter((block) => block.trim().length > 0)
    .join('\n\n')
}
